import useStory, { span, ease } from './useStory';
import { Explainer } from './GuideParts';

// GUIDE 8'S ANIMATION (29 Oct 2026): one top-floor 12 x 12 ft bedroom on
// Delhi's hottest afternoon, twice. The sun bakes the roof and heat comes
// in; each side shows how much of that heat its AC can take out. The 1 ton's
// bar stops short (its share comes from the calculator's engine, through
// sizeContent()); the 1.5 ton 5 Star's bar fills, and the room reaches 24°C.
// A drawing, not to scale. Plays once in view, rests fully drawn, Replay.

const SIDES = [
  { key: 'one', x: 18, label: '1 ton AC' },
  { key: 'five', x: 172, label: '1.5 ton 5 Star' },
];

export default function SizeRoom({ watch, sources }) {
  const story = useStory(20000);
  const t = story.t;
  const sun = ease(span(t, 0, 0.15));
  const roof = span(t, 0.08, 0.25);
  const heat = ease(span(t, 0.2, 0.42));
  const fill = {
    one: ease(span(t, 0.45, 0.68)) * (watch.oneTon / 100),
    five: ease(span(t, 0.75, 0.95)) * (watch.five / 100),
  };
  const verdict = { one: span(t, 0.66, 0.72), five: span(t, 0.93, 0.99) };
  const BAR_H = 92;
  const BAR_Y = 128;

  return (
    <Explainer title={watch.title} captions={watch.captions} story={story} note={watch.note} noteSrc={watch.noteSrc} sources={sources}>
      <svg viewBox="0 0 330 278" width="100%" role="img" className="mx-auto block h-auto w-full max-w-[480px] rounded-xl border border-gray-200 bg-gray-50"
        aria-label={`A top-floor bedroom under a hot roof. A 1 ton AC can take out only about ${watch.oneTon}% of the heat coming in. A 1.5 ton 5 Star takes out all of it, and the room reaches 24°C.`}>
        {/* the sun */}
        <g opacity={sun} transform={`translate(0 ${(1 - sun) * 8})`}>
          <circle cx="292" cy="26" r="14" fill="#fbbf24" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((d) => (
            <line key={d} x1="292" y1="5" x2="292" y2="0" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${d} 292 26)`} />
          ))}
        </g>
        <text x="16" y="24" fontSize="12" fill="#6b7280">Top floor, Delhi&apos;s hottest afternoon</text>

        {SIDES.map((s) => {
          const done = verdict[s.key];
          const blueH = BAR_H * fill[s.key];
          return (
            <g key={s.key}>
              {/* the roof slab, heating up */}
              <rect x={s.x} y="44" width="140" height="10" rx="2" fill="#9ca3af" />
              <rect x={s.x} y="44" width="140" height="10" rx="2" fill="#ea580c" opacity={roof * 0.85} />
              {/* the room */}
              <rect x={s.x} y="54" width="140" height="216" fill="#fff" stroke="#9ca3af" strokeWidth="1.5" />
              {/* heat coming down through the roof */}
              {[30, 70, 110].map((dx) => (
                <path key={dx} d={`M${s.x + dx} 60v${10 + heat * 12}`} stroke="#ea580c" strokeWidth="2.5" strokeLinecap="round"
                  opacity={heat} />
              ))}
              {/* the AC on the wall */}
              <rect x={s.x + 16} y="92" width="108" height="20" rx="6" fill="#fff" stroke="#6b7280" strokeWidth="1.5" />
              <text x={s.x + 70} y="106" fontSize="11.5" fill="#374151" textAnchor="middle" fontWeight="700">{s.label}</text>

              {/* two bars: heat coming in, and what the AC takes out */}
              <rect x={s.x + 26} y={BAR_Y} width="34" height={BAR_H} rx="4" fill="#f3f4f6" />
              <rect x={s.x + 26} y={BAR_Y + BAR_H * (1 - heat)} width="34" height={BAR_H * heat} rx="4" fill="#fdba74" />
              <rect x={s.x + 80} y={BAR_Y} width="34" height={BAR_H} rx="4" fill="#f3f4f6" />
              <rect x={s.x + 80} y={BAR_Y + BAR_H - blueH} width="34" height={blueH} rx="4" fill="#2563eb" />
              <line x1={s.x + 76} x2={s.x + 118} y1={BAR_Y} y2={BAR_Y} stroke="#9ca3af" strokeDasharray="3 3" opacity={heat} />
              <text x={s.x + 43} y={BAR_Y + BAR_H + 13} fontSize="10.5" fill="#6b7280" textAnchor="middle">Heat in</text>
              <text x={s.x + 97} y={BAR_Y + BAR_H + 13} fontSize="10.5" fill="#6b7280" textAnchor="middle">AC takes out</text>
              <text x={s.x + 97} y={BAR_Y + BAR_H - blueH - 4} fontSize="12" fill="#1d4ed8" textAnchor="middle" fontWeight="800"
                opacity={fill[s.key] > 0.05 ? 1 : 0}>{Math.round(fill[s.key] * 100)}%</text>

              {/* the verdict */}
              <text x={s.x + 70} y="258" fontSize="13" textAnchor="middle" fontWeight="800" fill="#111827" opacity={done}>
                {s.key === 'one' ? 'Room stays warm' : 'Room at 24°C'}
              </text>
            </g>
          );
        })}
      </svg>
    </Explainer>
  );
}
